import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router";

import { ApiError, apiFetch } from "@/lib/api";
import type { AlertInstanceHistoryResource } from "@/lib/api-types";

/**
 * The per-instance history view (Story 3.9, VC-3.9-08/09): the full
 * lifecycle — created, every send that mentioned it (with its real
 * provider message id, via the new send<->instance link), every snooze,
 * who acknowledged it and when, and its resolution and why it stopped.
 *
 * A dedicated small view rather than a reuse of `RecordPage.tsx` (Story
 * 2.5's own pattern): `RecordPage` renders one record's own field/value
 * rows plus a small set of actions — it has no shape for a chronological
 * timeline of several events, and stretching it to fit one would be the
 * kind of screen-owned special-casing this codebase's shared components
 * elsewhere are built to avoid needing.
 */

const KIND_LABELS: Record<string, string> = {
  created: "Created",
  sent: "Emailed",
  snoozed: "Snoozed",
  acknowledged: "Acknowledged",
  reopened: "Reopened",
  resolved: "Resolved",
};

const TIMESTAMP_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZone: "America/New_York",
});

export function AlertInstanceHistory() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    document.title = "Alert history — PropDesk";
  }, []);

  const query = useQuery({
    queryKey: ["alert-instances", id, "history"] as const,
    queryFn: () => apiFetch<AlertInstanceHistoryResource>(`/alert-instances/${id}/history`),
    enabled: Boolean(id),
  });

  return (
    <div className="max-w-record-column-max">
      <p className="mb-4">
        <Link to="/alerts" className="text-body text-ink-secondary hover:underline">
          &larr; Back to alerts
        </Link>
      </p>

      {query.isPending ? (
        <p className="text-body text-ink-secondary">Loading&hellip;</p>
      ) : query.isError ? (
        <p role="alert" className="text-body text-danger">
          {query.error instanceof ApiError ? query.error.message : "That alert could not be found."}
        </p>
      ) : (
        <>
          <h1 className="text-title font-semibold tracking-title text-ink md:hidden">{query.data.instance.sentence}</h1>
          <p className="mt-2 text-meta text-ink-secondary">
            {query.data.instance.state} {query.data.instance.entity_label ? `· ${query.data.instance.entity_label}` : ""}
          </p>

          <ol className="mt-6 flex flex-col gap-4 border-l border-border-hairline pl-4">
            {query.data.events.map((event, index) => (
              <li key={`${event.kind}-${event.at}-${index}`}>
                <p className="text-body-strong font-medium text-ink">{KIND_LABELS[event.kind] ?? event.kind}</p>
                <p className="text-meta text-ink-secondary">{TIMESTAMP_FORMATTER.format(new Date(event.at))}</p>
                <p className="mt-1 text-body text-ink">{event.detail}</p>
                {event.actor_display_name ? (
                  <p className="text-meta text-ink-secondary">By {event.actor_display_name}</p>
                ) : null}
                {event.provider_message_id ? (
                  <p className="text-meta text-ink-tertiary">Provider message id: {event.provider_message_id}</p>
                ) : null}
              </li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
}
